import styled from 'styled-components';
import { MouseEvent, PropsWithChildren, useCallback } from 'react';
import { MenuItem } from '@szhsin/react-menu';

import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import DrfqTakerOrderbookProvider from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-taker-orderbook-context';
import DrfqMakersProvider from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import { bigStone } from '@paradigm/design-system/src/colors';
import { useDrfqService } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import { useApi } from '@paradigm/api/src/fetch-api';
import useDrfqBbo from '@paradigm/features/src/unified-rfqs/contexts/use-drfq-bbo';
import { useHiddenLimitOrderFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import { useTrackAction } from '@paradigm/features/src/analytics';
import {
  useUMPlatformCreationDisabled,
  useUMPlatformKillDisabled,
} from '@paradigm/features/src/platform-state/contexts/providers';

import { RowIndexProvider } from '#/components/UnifiedRfqs/components/RfqRowBase';
import ExpandableStrategyDescription from '#/components/UnifiedRfqs/components/strategy-description/ExpandableStrategyDescription';
import ExpandableRfqRow from '#/components/UnifiedRfqs/components/ExpandableRfqRow';
import { useRfqExpansionState } from '#/components/UnifiedRfqs/rfqs/rfq-expansion-context';
import {
  Cell,
  MenuContainer,
  ROW_HEIGHT,
} from '#/components/rfq-common/TableRow';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import VerticalDots from '@paradigm/design-system/src/assets/VerticalDots';
import { toast } from '#/components/UnifiedRfqs/toast';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import GridCellBase, {
  GridCellProps,
} from '#/components/UnifiedRfqs/components/GridCellBase';
import ContextMenuProvider, {
  ContextMenu as BaseContextMenu,
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';

import { StrategyGreeksRow } from '#/components/UnifiedRfqs/components/StrategyGreeks';
import DrfqTakerRowAction from '#/components/UnifiedRfqs/drfq/DrfqTakerRowAction';
import DrfqMakerRowAction from '#/components/UnifiedRfqs/drfq/DrfqMakerRowAction';
import MakerOrders from '#/components/UnifiedRfqs/drfq/MakerOrders';
import { stopPropagation } from '#/components/UnifiedRfqs/drfq/common';
import { useRfqRowView } from '@paradigm/features/src/unified-rfqs/contexts/rfq-row-view';
import {
  CreationTimeCell,
  LastTradeCell,
} from '#/components/UnifiedRfqs/components/RfqRowCommon';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';
import { findBaseProductCode } from '@paradigm/features/src/products/domain';
import { joinProductCodes } from '@paradigm/features/src/unified-rfqs/utils/productCodes';
import DrfqDetailsModalProvider from '#/components/UnifiedRfqs/drfq/taker-rfq-details/DrfqDetailsModalProvider';
import TakerOrders from './TakerOrders';

function ContextMenu() {
  const { togglePinnedRfq, openDetailsModal, createRfq } =
    useContextMenuActions();
  const drfq = useDrfqRfq();
  const { isPinnedRfq } = useContextMenuView();
  const drfqService = useDrfqService();
  const [, cancelDrfqApi] = useApi(drfqService.cancelDrfq);
  const isHiddenLimitOrderEnabled = useHiddenLimitOrderFeatureFlag();
  const track = useTrackAction();
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const isPlatformKillDisabled = useUMPlatformKillDisabled();

  const handleHiddenLimit = useCallback(() => {
    if (!drfq.id) return;
    track('submit hidden limit order');
    openDetailsModal();
  }, [drfq.id, openDetailsModal, track]);

  const cancelRfq = useCallback(() => {
    cancelDrfqApi({ rfqId: drfq.id }, (resp) => {
      if (!resp.ok) {
        toast(resp.message);
      }
    });
  }, [cancelDrfqApi, drfq.id]);
  const datadogPrefix = 'Rfq Table Drfq Row Context Menu';

  const isRfqOpen = !['CLOSED', 'RECENTLY_CLOSED'].includes(drfq.state);

  return (
    <BaseContextMenu>
      <MenuItem onClick={togglePinnedRfq}>
        {isPinnedRfq ? 'Unpin' : 'Pin'}
      </MenuItem>
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={() => createRfq('OB')}
      />
      {drfq.role === 'TAKER' && (
        <>
          {isHiddenLimitOrderEnabled && isRfqOpen && (
            <MenuItem
              onClick={handleHiddenLimit}
              disabled={isPlatformCreationDisabled}
            >
              Hidden Limit (Beta)
            </MenuItem>
          )}
          <MenuItem onClick={cancelRfq} disabled={isPlatformKillDisabled}>
            Cancel RFQ
          </MenuItem>
          <MenuItem onClick={openDetailsModal}>View Details</MenuItem>
        </>
      )}
    </BaseContextMenu>
  );
}

export default function DrfqRow() {
  const drfq = useDrfqRfq();
  let content = (
    <DrfqMakersProvider drfq={drfq}>
      <DrfqRowContent />
    </DrfqMakersProvider>
  );

  if (drfq.role === 'TAKER') {
    content = (
      <DrfqTakerOrderbookProvider>
        <DrfqRowContent />
        <DrfqDetailsModalProvider />
      </DrfqTakerOrderbookProvider>
    );
  }

  return (
    <ContextMenuProvider>
      <ExpandableWrapper>{content}</ExpandableWrapper>
      <ContextMenu />
    </ContextMenuProvider>
  );
}

function ExpandableWrapper({ children }: PropsWithChildren) {
  const drfq = useDrfqRfq();
  const contextMenu = useContextMenuActions();

  const onContextMenu = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const anchorPoint = { x: event.clientX, y: event.clientY };
      contextMenu.open({
        rfqId: drfq.id,
        anchorPoint,
        productCode: findBaseProductCode(drfq.product_codes),
      });
    },
    [contextMenu, drfq.id, drfq.product_codes],
  );
  const isMobile = useIsMobile();
  return (
    <Container>
      <ExpandableRfqRow
        rfqId={drfq.id}
        onContextMenu={onContextMenu}
        isExpandable={drfq.state !== 'RECENTLY_CLOSED' && !isMobile}
      >
        {children}
      </ExpandableRfqRow>
    </Container>
  );
}

export function DrfqRowContent() {
  const drfq = useDrfqRfq();
  const bbo = useDrfqBbo(drfq.id);
  const { openDetailsModal } = useContextMenuActions();
  const { type, product, description, id, productCodes } = useRfqRowView();

  const isExpanded =
    useRfqExpansionState(drfq.id) && drfq.state !== 'RECENTLY_CLOSED';
  const greeksRow = (
    <StrategyGreeksRow bbo={bbo} venue={drfq.venue} height={ROW_HEIGHT} />
  );
  const isMobile = useIsMobile();
  const contextMenu = useContextMenuActions();

  return (
    <>
      {isMobile && (
        <MenuContainer
          role="cell"
          onClick={(event) =>
            contextMenu.open({
              anchorPoint: { x: event.clientX, y: event.clientY },
              rfqId: drfq.id,
            })
          }
        >
          <VerticalDots />
        </MenuContainer>
      )}
      <IdContainer>
        <Type mobile>{type.formatted}</Type>
        {isExpanded && !isMobile && (
          <IdLabel onClick={stopPropagation()}>{id.formatted}</IdLabel>
        )}
      </IdContainer>
      <Product
        area="product"
        mobile
        role="cell"
        data-testid="product"
        title={
          productCodes.value.length > 1
            ? joinProductCodes(productCodes.value)
            : undefined
        }
      >
        {productCodes.value.length > 1
          ? productCodes.formatted
          : product.formatted}
      </Product>
      <ExpandableStrategyDescription
        onViewAll={openDetailsModal}
        isExpanded={isExpanded}
      >
        {description}
      </ExpandableStrategyDescription>
      <Cell area="action" mobile css="position: relative;  height: 100%;">
        <RowIndexProvider value={0}>
          {drfq.role === 'TAKER' ? (
            <DrfqTakerRowAction />
          ) : (
            <DrfqMakerRowAction />
          )}
        </RowIndexProvider>
      </Cell>
      <LastTradeCell />
      <Cell>{drfq.role === 'MAKER' ? <MakerOrders /> : <TakerOrders />}</Cell>
      {greeksRow}
      <CreationTimeCell />
    </>
  );
}

const Container = styled.div`
  border-bottom: 1px solid ${bigStone};
  cursor: pointer;
`;

const Product = styled(GridCellBase)`
  height: ${ROW_HEIGHT}px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  justify-content: center;
  align-items: center;
  font-size: 0.8125rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-bottom: 1rem;
  }
`;

const Type = styled(Cell)`
  font-size: 13px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-items: end;
  }
`;
const IdLabel = styled(GridCellBase)`
  font-size: 11px;
  color: #ffffff;
  opacity: 0.5;
  margin-bottom: 4px;
  width: 100%;
`;

const IdContainer = styled(GridCellBase).attrs<GridCellProps>(() => ({
  area: 'type',
  mobile: true,
}))<GridCellProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  > div {
    white-space: nowrap;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: auto;
    padding-top: 1rem;
  }
`;
